import React from "react";
import styled from "styled-components";
import { colors, typography } from "../styles/designSystem";
import { SubsectionHeadline } from "../components/Typography";
import { RecoveryEngineDiagram } from "../components/RecoveryEngineDiagram";

import NeboHero from "../pngs/Nebo_hero.png";
import NeboAppHome from "../pngs/Nebo_App_Home.png";
import NeboApp from "../pngs/Nebo_app_.png";
import NeboMarketing from "../pngs/Nebo_Marketing.png";

const PageWrapper = styled.div`
    max-width: 900px;
    margin: 0 auto;
    padding: 2rem 1.5rem 6rem;
`;

const HeroSection = styled.section`
    text-align: center;
    padding: 3rem 0 3rem;
    border-bottom: 1px solid ${({ theme }) => theme.borderLight};
    margin-bottom: 4rem;
`;

const HeroLabel = styled.span`
    display: inline-block;
    font-size: ${typography.fontSize[100]};
    font-weight: ${typography.fontWeight.semibold};
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: ${colors.cyan[500]};
    background: ${colors.cyan[500]}15;
    padding: 6px 16px;
    border-radius: 999px;
    margin-bottom: 1.5rem;
`;

const HeroTitle = styled.h1`
    font-size: ${typography.fontSize[900]};
    font-weight: ${typography.fontWeight.bold};
    color: ${({ theme }) => theme.text};
    line-height: ${typography.lineHeight[200]};
    margin: 0 0 1.5rem;

    @media (max-width: 768px) {
        font-size: ${typography.fontSize[700]};
    }
`;

const HeroSubtitle = styled.p`
    font-size: ${typography.fontSize[400]};
    line-height: 1.8;
    color: ${({ theme }) => theme.subtleText};
    max-width: 640px;
    margin: 0 auto 2rem;
`;

const ExternalLink = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-size: ${typography.fontSize[300]};
    font-weight: ${typography.fontWeight.semibold};
    color: ${colors.cyan[500]};
    text-decoration: none;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 0.8;
        color: ${colors.cyan[500]};
    }
`;

const ContentSection = styled.section`
    margin-bottom: 4.5rem;
`;

const SectionTitle = styled(SubsectionHeadline)`
    margin-bottom: 1.5rem;
`;

const Paragraph = styled.p`
    font-size: ${typography.fontSize[300]};
    line-height: 1.8;
    color: ${({ theme }) => theme.subtleText};
    margin: 0 0 1.25rem;
`;

const TechGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    margin-top: 1rem;
`;

const TechChip = styled.span`
    font-size: ${typography.fontSize[100]};
    font-weight: ${typography.fontWeight.medium};
    color: ${({ theme }) => theme.text};
    background: ${({ theme }) => theme.cardBackground};
    border: 1px solid ${({ theme }) => theme.borderLight};
    border-radius: 999px;
    padding: 0.4rem 1rem;
`;

const ImageGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
    margin: 2.5rem 0;

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
    }
`;

const ImageCaption = styled.span`
    display: block;
    text-align: center;
    font-size: ${typography.fontSize[100]};
    color: ${({ theme }) => theme.subtleText};
    margin-top: 0.75rem;
`;

const DiagramWrapper = styled.div`
    margin: 2.5rem 0;
`;

const ScreenshotFull = styled.img`
    width: 100%;
    border-radius: 12px;
    border: 1px solid ${({ theme }) => theme.borderLight};
    margin: 2.5rem 0;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const ScreenshotGrid = styled.img`
    width: 100%;
    border-radius: 12px;
    border: 1px solid ${({ theme }) => theme.borderLight};
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const Nebo = () => {
    return (
        <PageWrapper>
            <HeroSection>
                <HeroLabel>Founder &amp; Software Engineer &middot; Jun 2020 &ndash; Present</HeroLabel>
                <HeroTitle>Nebo</HeroTitle>
                <HeroSubtitle>
                    An asset registration and recovery platform I build end-to-end — a
                    cross-platform mobile app, a Supabase and PostgreSQL backend with
                    row-level security, an AI-powered recovery engine, and a Next.js
                    marketing site.
                </HeroSubtitle>
                <ExternalLink
                    href="https://projectnebo.com"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Visit projectnebo.com &#8599;
                </ExternalLink>
            </HeroSection>

            <ScreenshotFull src={NeboHero} alt="Nebo — product hero" />

            <ContentSection>
                <SectionTitle>Overview</SectionTitle>
                <Paragraph>
                    Most bike trackers stop at "we know where it went." Nebo is built
                    around the next question: actually getting the bike back. I own the
                    project end-to-end as founder and product engineer — the React Native
                    app, the backend and data model, the AI recovery engine, and the
                    marketing site.
                </Paragraph>
            </ContentSection>

            <ImageGrid>
                <div>
                    <ScreenshotGrid src={NeboAppHome} alt="Nebo mobile app — home / live tracking" />
                    <ImageCaption>Live tracking & device status</ImageCaption>
                </div>
                <div>
                    <ScreenshotGrid src={NeboApp} alt="Nebo mobile app — theft mode & recovery handoff" />
                    <ImageCaption>Theft trigger & recovery handoff</ImageCaption>
                </div>
            </ImageGrid>

            <ContentSection>
                <SectionTitle>Mobile App</SectionTitle>
                <Paragraph>
                    Built in React Native so iOS and Android share one codebase and one
                    pairing flow. The app handles account & device pairing, live tracking,
                    a theft-mode trigger, and the recovery handoff — the moment where the
                    product stops being "an app" and becomes a service.
                </Paragraph>
                <Paragraph>
                    Location updates are batched and motion-gated rather than streamed.
                    A parked bike doesn't need a fix every ten seconds; a moving bike
                    does. High-rate updates only kick in after a theft trigger, which
                    keeps the experience predictable without sacrificing fidelity when
                    it actually matters.
                </Paragraph>
                <Paragraph>
                    Recovery flow escalates beyond the app. When the user triggers theft
                    mode, the app opens a guided handoff — evidence, last-known route,
                    and the recovery tooling — so the user is not alone staring at a
                    dot on a map.
                </Paragraph>
            </ContentSection>

            <DiagramWrapper>
                <RecoveryEngineDiagram />
            </DiagramWrapper>

            <ContentSection>
                <SectionTitle>Recovery Match Engine</SectionTitle>
                <Paragraph>
                    Most stolen bikes don't get recovered by GPS — they get recovered
                    when someone spots them listed for resale. The match engine I
                    designed scans Facebook Marketplace, OfferUp, Craigslist, and eBay
                    for listings that look like the user's bike, runs each candidate
                    through Claude vision to compare it against the owner's photos,
                    and surfaces the strongest hits as a Twilio SMS with a link straight
                    to the listing — so the user can act in minutes, not days.
                </Paragraph>
                <Paragraph>
                    The non-obvious decision was keeping the human in the loop. Auto-flagging
                    "this is your bike, go report it" on a 70% confidence is the wrong
                    default — false positives cost the user more than missed hits. The
                    model returns a ranked, scored list with its reasoning, and the user
                    makes the call.
                </Paragraph>
            </ContentSection>

            <ScreenshotFull src={NeboMarketing} alt="Nebo marketing site — projectnebo.com" />

            <ContentSection>
                <SectionTitle>Marketing Website</SectionTitle>
                <Paragraph>
                    Next.js 16 (App Router) + TypeScript + Tailwind v4, deployed on Vercel
                    at projectnebo.com. Resend handles transactional email for the
                    contact and onboarding flows. The mobile-first nav and multi-page
                    SEO structure are built around two very different audiences with
                    different content priorities — the IA splits accordingly without
                    forcing one to read the other's pages.
                </Paragraph>
                <Paragraph>
                    Two details earned their weight. First, the OG and Twitter share
                    images are generated dynamically with next/og — every page gets a
                    tailored card without me hand-baking PNGs. Second, the animated
                    product diagram on the homepage is a single SVG driven from
                    structured data, so the marketing site can't drift out of sync
                    with the actual product.
                </Paragraph>
            </ContentSection>

            <ContentSection>
                <SectionTitle>Hardware & Enclosure</SectionTitle>
                <Paragraph>
                    Nebo is a physical product, so the software has to live somewhere.
                    I designed the custom PCB and the enclosure that hides it inside
                    the bike, and brought the device through FCC certification.
                </Paragraph>
                <Paragraph>
                    The hardware proved out the form factor and gave the software
                    something real to talk to — but the bulk of my time, and the
                    interesting craft, lives on the software side.
                </Paragraph>
            </ContentSection>

            <ContentSection>
                <SectionTitle>Tech Stack</SectionTitle>
                <TechGrid>
                    <TechChip>React Native</TechChip>
                    <TechChip>Claude API (Vision)</TechChip>
                    <TechChip>Twilio SMS</TechChip>
                    <TechChip>Next.js 16</TechChip>
                    <TechChip>TypeScript</TechChip>
                    <TechChip>Tailwind v4</TechChip>
                    <TechChip>Resend</TechChip>
                    <TechChip>Vercel</TechChip>
                    <TechChip>Figma</TechChip>
                    <TechChip>FCC Certified</TechChip>
                </TechGrid>
            </ContentSection>

        </PageWrapper>
    );
};

export default Nebo;
